import { Tag, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/**
 * Volume Discount tier editor — up to 3 tiers. Only shown when quantity > 1.
 * Writes to `draft.volume_discounts` as `[{min_qty, discount_pct}]`. Tiers
 * are applied as an eBay VOLUME_DISCOUNT item promotion at publish time.
 */
const MAX_TIERS = 3;
const DEFAULT_TIERS = [
  { min_qty: 2, discount_pct: "" },
  { min_qty: 3, discount_pct: "" },
  { min_qty: 5, discount_pct: "" },
];

export default function VolumeDiscounts({ draft, patch }) {
  const qty = parseInt(draft?.quantity, 10) || 1;
  if (qty <= 1) return null;

  const tiers = Array.isArray(draft?.volume_discounts) && draft.volume_discounts.length
    ? draft.volume_discounts
    : [];

  const setTier = (idx, next) => {
    const working = [...(tiers.length ? tiers : DEFAULT_TIERS.slice(0, 1))];
    while (working.length <= idx) working.push({ min_qty: "", discount_pct: "" });
    working[idx] = { ...working[idx], ...next };
    patch({ volume_discounts: working });
  };

  const removeTier = (idx) => {
    const next = tiers.filter((_, i) => i !== idx);
    patch({ volume_discounts: next });
  };

  const addTier = () => {
    const taken = new Set(tiers.map((t) => parseInt(t.min_qty, 10) || 0));
    const suggestion = [2, 3, 5, 10].find((n) => !taken.has(n)) || "";
    patch({ volume_discounts: [...tiers, { min_qty: suggestion, discount_pct: "" }] });
  };

  const rows = tiers.length ? tiers : [{ min_qty: 2, discount_pct: "" }];
  const canAdd = rows.length < MAX_TIERS;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="volume-discounts-panel">
      <div className="mb-2 flex items-center gap-2">
        <Tag className="h-3.5 w-3.5 text-emerald-600" />
        <Label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">
          Volume discount tiers
        </Label>
      </div>
      <div className="space-y-1.5">
        {rows.map((t, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`volume-tier-${i}`}>
            <span className="font-mono text-[11px] text-slate-500">Buy</span>
            <Input
              value={t.min_qty ?? ""}
              onChange={(e) => setTier(i, { min_qty: e.target.value.replace(/[^0-9]/g, "") })}
              placeholder="2"
              inputMode="numeric"
              className="h-7 w-14 text-center font-mono text-xs"
              data-testid={`volume-tier-qty-${i}`}
              aria-label={`Tier ${i + 1} minimum quantity`}
            />
            <span className="font-mono text-[11px] text-slate-500">or more → save</span>
            <Input
              value={t.discount_pct ?? ""}
              onChange={(e) => setTier(i, { discount_pct: e.target.value.replace(/[^0-9.]/g, "") })}
              placeholder="10"
              inputMode="decimal"
              className="h-7 w-16 text-center font-mono text-xs"
              data-testid={`volume-tier-pct-${i}`}
              aria-label={`Tier ${i + 1} discount percent`}
            />
            <span className="font-mono text-[11px] text-slate-500">%</span>
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => removeTier(i)}
                className="ml-auto rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                data-testid={`volume-tier-remove-${i}`}
                aria-label="Remove tier"
                title="Remove this tier"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>
      {canAdd && (
        <Button
          type="button"
          onClick={addTier}
          variant="outline"
          size="sm"
          className="mt-2 h-7 rounded-full text-[11px]"
          data-testid="volume-tier-add"
        >
          + Add tier ({MAX_TIERS - rows.length} left)
        </Button>
      )}
      <p className="mt-2 text-[10px] text-slate-500">
        Applied automatically at eBay checkout as a VOLUME_DISCOUNT promotion. Requires sell.marketing scope on your eBay account.
      </p>
    </div>
  );
}
